/**
 * driftking-core.js
 * Pure business logic for the Mini DriftKing V2 bundle hero:
 *  - quantity 1/2/3 with an independent per-unit color select
 *  - totals/compare computed from the SELECTED variants (never hardcoded)
 *  - synchronized discount %, and correct /cart/add.js aggregation
 * UMD so it loads as a classic theme asset (window.DriftKingCore) and is
 * require()able by the node/jsdom tests.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.DriftKingCore = factory();
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  // Stable QA selectors — do not rename (parent / QA rely on these).
  const ALL_VARIANTS_SELECTORS = {
    root: '[data-dk-root]',
    quantity: '[data-dk-quantity]',
    color: '[data-dk-color]',
    buy: '[data-dk-buy]',
    total: '[data-dk-total]',
    productJson: '[data-dk-product-json]',
    gallery: '[data-dk-gallery]',
    thumb: '[data-dk-thumb]',
    status: '[data-dk-status]',
  };

  /** Ordered color options from product.variants (option1), with real variant ids. */
  function getColorOptions(product) {
    return (product.variants || []).map((v) => ({ id: v.id, name: v.option1 || v.title }));
  }

  /** Controlled first-available variant id (fallback = first variant). */
  function firstAvailableId(product) {
    const vars = product.variants || [];
    const first = vars.find((v) => v.available !== false);
    return first ? first.id : vars[0]?.id;
  }

  /**
   * Initial selection for `units` slots.
   * @param {object} product
   * @param {number} units quantity (1..3)
   * @param {string|null} urlVariantId from ?variant=
   * @returns {number[]} variant ids, one per slot
   */
  function resolveInitialIds(product, units, urlVariantId) {
    const count = Math.max(1, Math.min(3, Number(units) || 1));
    const fallback = firstAvailableId(product);
    const ids = (product.variants || []).map((v) => v.id);
    const chosen = urlVariantId != null && ids.includes(Number(urlVariantId)) ? Number(urlVariantId) : fallback;
    return Array(count).fill(chosen);
  }

  /**
   * Aggregate per-slot variant ids into cart lines [{id, quantity}],
   * merging duplicates (e.g. both slots Metallic Sky => quantity 2).
   */
  function aggregateItems(variantIds) {
    const map = new Map();
    (variantIds || []).forEach((id) => map.set(id, (map.get(id) || 0) + 1));
    return [...map.entries()].map(([id, quantity]) => ({ id, quantity }));
  }

  /** Resolve a variant object by id. */
  function variantById(product, id) {
    return (product.variants || []).find((v) => v.id === Number(id));
  }

  /**
   * Totals for the current selection, sourced from real selected variants.
   * No made-up volume discount: price = sum(unit.price), compare = sum(unit.compare).
   * @returns {{price:number, compare:number, save:number, savePct:number}}
   */
  function computeTotals(product, variantIds) {
    let price = 0;
    let compare = 0;
    (variantIds || []).forEach((id) => {
      const v = variantById(product, id) || {};
      price += Number(v.price) || 0;
      compare += Number(v.compare_at_price) || Number(v.price) || 0;
    });
    const save = Math.max(0, compare - price);
    const savePct = compare > 0 ? Math.round((save / compare) * 100) : 0;
    return { price, compare, save, savePct };
  }

  /** Slide index in product.media for a variant's featured_media id. -1 if absent. */
  function getSlideIndex(product, variantId) {
    const v = variantById(product, variantId);
    if (!v || !v.featured_media || !product.media) return -1;
    return product.media.findIndex((m) => m.id === v.featured_media.id);
  }

  return {
    getColorOptions,
    firstAvailableId,
    resolveInitialIds,
    aggregateItems,
    variantById,
    computeTotals,
    getSlideIndex,
    ALL_VARIANTS_SELECTORS,
  };
});
